import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ZUserReviewInsert, UserReviewInsert } from '@/contracts/userReviews';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Star } from 'lucide-react';
import { useCreateReview } from '@/hooks/useUserReviews';

interface ReviewFormProps {
  productId: string;
  userId: string;
  onSuccess?: () => void;
}

/**
 * Review Form Component
 * Form for submitting product reviews with validation
 */
export function ReviewForm({ productId, userId, onSuccess }: ReviewFormProps) {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const createReview = useCreateReview();

  const form = useForm<UserReviewInsert>({
    resolver: zodResolver(ZUserReviewInsert),
    defaultValues: {
      product_id: productId,
      user_id: userId,
      rating: 0,
      title: '',
      content: '',
    },
  });

  const selectedRating = form.watch('rating');
  const displayRating = hoveredRating || selectedRating;

  const onSubmit = async (data: UserReviewInsert) => {
    if (data.rating === 0) {
      form.setError('rating', { message: 'Please select a rating' });
      return;
    }

    await createReview.mutateAsync(data);
    form.reset({
      product_id: productId,
      user_id: userId,
      rating: 0,
      title: '',
      content: '',
    });
    onSuccess?.();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Write a Review</CardTitle>
        <CardDescription>
          Share your experience with this product. Reviews are moderated before appearing publicly.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Rating Stars */}
            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rating *</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => field.onChange(star)}
                          onMouseEnter={() => setHoveredRating(star)}
                          onMouseLeave={() => setHoveredRating(null)}
                          className="p-1 hover:scale-110 transition-transform"
                        >
                          <Star
                            className={`h-8 w-8 ${
                              star <= displayRating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-muted-foreground'
                            }`}
                          />
                        </button>
                      ))}
                      {selectedRating > 0 && (
                        <span className="ml-3 text-sm text-muted-foreground">
                          {selectedRating} {selectedRating === 1 ? 'star' : 'stars'}
                        </span>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Review Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Review Title *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Sum up your experience in one line"
                      maxLength={200}
                      {...field}
                    />
                  </FormControl>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <FormMessage />
                    <span>{field.value.length}/200</span>
                  </div>
                </FormItem>
              )}
            />

            {/* Review Content */}
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Review *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Tell us about your experience with this product. What did you like? What could be improved?"
                      rows={6}
                      maxLength={2000}
                      {...field}
                    />
                  </FormControl>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <FormMessage />
                    <span>{field.value.length}/2000</span>
                  </div>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={createReview.isPending}
              className="w-full"
            >
              {createReview.isPending ? 'Submitting...' : 'Submit Review'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
